import { useEffect, useRef, useState } from "react";
import { SiteSwitcher } from "../../../services/SiteSwitcher";
import { ContentService } from "../../../services/ContentService";
import { IContentTemplateHandle, IComponent, IContentComponent, ILayoutTemplate, IVersion, IVersionSection, IScriptAndStyle } from "../../../models/Interfaces";
import { useParams } from "react-router-dom";
import styles from "./designer.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faEdit, faFile, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import SectionAddEdit from "./SectionAddEdit";
import GridStackTemplate from "./GridStackTemplate";
import ComponentToolbar from "./ComponentToolbar";
import LayoutPreview from "./LayoutPreview";
import ComponentSidebar from "./ComponentSidebar";
import { ContentEditorService } from "../../../services/ContentEditorService";
import ContentComponentEditor from "./ContentComponentEditor";
import ColumnsTemplate from "./ColumnTemplate";
import Loading from "../../components/common/Loading";
import AppEmptyState from "../../components/common/AppEmptyState";
import ColourPicker from "../../components/common/ColourPicker";
import SiteMediaPicker from "../../components/common/SiteMediaPicker";
import { Button, Col, FormCheck, FormControl, FormLabel, FormSelect, Nav, Row, Tab } from "react-bootstrap";
import AceEditor from "react-ace";
import 'ace-builds/src-noconflict/ext-command_bar';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/theme-sqlserver';
import { ScriptIncludeType, TemplateType, VersionStatus } from "../../../config/Enums";
import { useConfirm } from "../../../config/ConfirmProvider";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";
import { componentCopySource, emptyCopyColumn, copyGridPosition } from "./componentDuplication";
import { ComponentRevision, layoutsMatch, mergeFetchedSections, reindexSections, sectionInsertionOrder, replaceSectionComponents, withLiveLayout } from "./sectionContent";
import AppModal from "../../components/common/AppModal";
import NewSectionWizard from "../../../AIHelper/NewSectionWizard";
import { AIContentGenerationService } from "../../../AIHelper/AIContentGenerationService";
import { IAISectionCreationBrief } from "../../../AIHelper/types";
import { isSiteAiConnected } from "../../../AIHelper/siteAi";
import { usePreviewUrl } from "../../../services/PreviewDomain";
import { SiteService } from "../../../services/SiteService";
import { buildPageSectionFrame } from "../../components/common/editorSiteFrame";

export const iframeMessageHandlers = new Map<string, (data: any) => void>();

const toRgba = (colour: string, opacity: number): string => {
  const trimmed = colour.trim();

  if (trimmed.startsWith("#")) {
    let hex = trimmed.slice(1);

    if (hex.length === 3) {
      hex = hex.split("").map(ch => `${ch}${ch}`).join("");
    }

    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
  }

  const rgbMatch = trimmed.match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
  if (rgbMatch) {
    return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`;
  }

  const rgbaMatch = trimmed.match(/^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([0-9.]+)\s*\)$/i);
  if (rgbaMatch) {
    return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
  }

  return trimmed;
};

const getBackgroundType = (version?: IVersion | null): string => {
  const hasColour = (version?.PageBackgroundColour ?? "").trim().length > 0;
  const hasImage = (version?.PageBackgroundImage ?? "").trim().length > 0;

  if (hasColour && hasImage) {
    return "colour-image";
  }

  if (hasImage) {
    return "image";
  }

  if (hasColour) {
    return "colour";
  }

  return "none";
};

const getVersionCanvasStyle = (version?: IVersion | null): React.CSSProperties => {
  const style: React.CSSProperties = {};
  const backgroundColour = version?.PageBackgroundColour?.trim() ?? "";
  const backgroundImage = version?.PageBackgroundImage?.trim() ?? "";
  const overlayColour = version?.PageBackgroundOverlayColour?.trim() ?? "";
  const overlayOpacity = version?.PageBackgroundOverlayOpacity ?? null;

  if (backgroundColour.length > 0) {
    style.backgroundColor = backgroundColour;
  }

  if (backgroundImage.length > 0) {
    const layers: string[] = [];

    if (overlayColour.length > 0) {
      const overlayValue = overlayOpacity !== null
        ? toRgba(overlayColour, overlayOpacity)
        : overlayColour;
      layers.push(`linear-gradient(${overlayValue}, ${overlayValue})`);
    }

    layers.push(`url("${backgroundImage}")`);

    style.backgroundImage = layers.join(", ");
    style.backgroundSize = version?.PageBackgroundImageStyle ?? "cover";
    style.backgroundPosition = version?.PageBackgroundImagePosition ?? "center center";
    style.backgroundRepeat = version?.PageBackgroundImageRepeat ?? "no-repeat";
    style.backgroundAttachment = version?.PageBackgroundImageAttachment ?? "scroll";
  }

  return style;
};

const mergeVersionPresentationSettings = (version: IVersion, settingsSource: IVersion): IVersion => ({
  ...version,
  DisplayBreadcrumbs: settingsSource.DisplayBreadcrumbs,
  PageStyle: settingsSource.PageStyle,
  PageScript: settingsSource.PageScript,
  PageClass: settingsSource.PageClass,
  PageBackgroundImage: settingsSource.PageBackgroundImage,
  PageBackgroundImageStyle: settingsSource.PageBackgroundImageStyle,
  PageBackgroundColour: settingsSource.PageBackgroundColour,
  PageBackgroundImagePosition: settingsSource.PageBackgroundImagePosition,
  PageBackgroundImageRepeat: settingsSource.PageBackgroundImageRepeat,
  PageBackgroundImageAttachment: settingsSource.PageBackgroundImageAttachment,
  PageBackgroundOverlayColour: settingsSource.PageBackgroundOverlayColour,
  PageBackgroundOverlayOpacity: settingsSource.PageBackgroundOverlayOpacity,
});

const Designer = () => {
  const { siteid, versionid } = useParams();

  const siteSwitchRef = useRef<SiteSwitcher>();
  const contentSvcRef = useRef<ContentService>();
  const contentEditSvcRef = useRef<ContentEditorService>();
  const aiGenerationSvcRef = useRef<AIContentGenerationService>();
  const siteSvcRef = useRef<SiteService>();

  const templateRefs = useRef<{ [key: string]: IContentTemplateHandle | null }>({});

  const [versionDetail, setVersionDetail] = useState<IVersion>();
  const [previewDomain, setPreviewDomain] = useState<string | null>(null);
  const resolvedPreviewUrl = usePreviewUrl(versionDetail?.PreviewUrl ?? "", previewDomain ?? versionDetail?.Domain);
  const [sections, setSections] = useState<Array<IVersionSection>>([]);
  const sectionsRef = useRef<Array<IVersionSection>>([]);
  const layoutClock = useRef(0);
  const componentRevisions = useRef<Map<number, ComponentRevision>>(new Map());
  const removedComponentGenerations = useRef<Map<number, number>>(new Map());
  const removedSectionGenerations = useRef<Map<number, number>>(new Map());
  const layoutFlights = useRef<Map<number, Promise<void>>>(new Map());
  const [components, setComponents] = useState<Array<IComponent>>([]);
  const [layoutTemplates, setLayoutTemplates] = useState<Array<ILayoutTemplate>>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [showSectionOptions, setShowSectionOptions] = useState<boolean>(false);
  const [showSectionWizard, setShowSectionWizard] = useState<boolean>(false);
  const [showPageSettings, setShowPageSettings] = useState<boolean>(false);
  const [layoutPreviewMode, setLayoutPreviewMode] = useState<"desktop" | "phone" | null>(null);
  const [selectedSection, setSelectedSection] = useState<IVersionSection | null>(null);
  const sectionMoveInFlight = useRef(false);
  const [movingSection, setMovingSection] = useState(false);
  const [pendingSectionOrder, setPendingSectionOrder] = useState<number>(0);
  const [addAbove, setAddAbove] = useState<boolean>();
  const [selectedContentComponent, setSelectedContentComponent] = useState<IContentComponent | null>(null);
  const inlineDirtyComponentsRef = useRef(new Set<number>());
  const onInlineDirtyChange = (id: number, dirty: boolean) => {
    if (dirty) inlineDirtyComponentsRef.current.add(id);
    else inlineDirtyComponentsRef.current.delete(id);
  };
  const nestedComponentEditorRef = useRef(false);
  const onNestedEditorChange = (open: boolean) => { nestedComponentEditorRef.current = open; };
  const [selectedProperty, setSelectedProperty] = useState<string>();
  const [duplicatingComponentId, setDuplicatingComponentId] = useState<number>();
  const duplicatingComponentRef = useRef(false);
  const [componentDrafts, setComponentDrafts] = useState<Record<number, IContentComponent>>({});
  const [siteStyles, setSiteStyles] = useState<Array<CSSStyleRule>>([]);
  const [pageSettingsDraft, setPageSettingsDraft] = useState<IVersion | null>(null);
  const [pageBackgroundType, setPageBackgroundType] = useState<string>("none");
  const [showGenerationOverlay, setShowGenerationOverlay] = useState<boolean>(false);
  const [generationMessage, setGenerationMessage] = useState<string>("Generating section...");
  const [aiAvailable, setAiAvailable] = useState<boolean>(false);

  const { requestConfirmation } = useConfirm();

  const commitSections = (updater: (current: Array<IVersionSection>) => Array<IVersionSection>): void => {
    const next = updater(sectionsRef.current);
    sectionsRef.current = next;
    setSections(next);
  };

  const noteComponentRevision = (component: IContentComponent): void => {
    const generation = ++layoutClock.current;
    componentRevisions.current.set(component.Id, { generation, component });
    removedComponentGenerations.current.delete(component.Id);
  };

  const noteComponentRemoved = (componentId: number): void => {
    const generation = ++layoutClock.current;
    removedComponentGenerations.current.set(componentId, generation);
    componentRevisions.current.delete(componentId);
    inlineDirtyComponentsRef.current.delete(componentId);
    setSelectedContentComponent((current) => current?.Id === componentId ? null : current);
    setComponentDrafts((current) => {
      if (!current[componentId]) return current;
      const remaining = { ...current };
      delete remaining[componentId];
      return remaining;
    });
  };

  const changeSectionComponents = (
    sectionId: number,
    updater: (components: Array<IContentComponent>) => Array<IContentComponent>
  ): void => {
    commitSections((current) => {
      const previous = current.find((section) => section.Id === sectionId)?.ContentComponents ?? [];
      const next = replaceSectionComponents(current, sectionId, updater);
      const updated = next.find((section) => section.Id === sectionId)?.ContentComponents ?? [];
      const updatedIds = new Set(updated.map((component) => component.Id));

      updated.forEach((component) => {
        const prior = previous.find((item) => item.Id === component.Id);
        if (prior !== component) {
          noteComponentRevision(component);
        }
      });

      previous.forEach((component) => {
        if (!updatedIds.has(component.Id)) {
          noteComponentRemoved(component.Id);
        }
      });

      return next;
    });
  };

  const enqueueLayoutSave = (componentIds: Array<number>, save: () => Promise<unknown>): void => {
    const previous = Promise.all(
      componentIds.map((componentId) => layoutFlights.current.get(componentId) ?? Promise.resolve())
    ).then(() => undefined);

    const next = previous
      .catch(() => undefined)
      .then(() => save())
      .then(() => undefined)
      .catch(() => undefined);

    componentIds.forEach((componentId) => layoutFlights.current.set(componentId, next));
    void next.finally(() => {
      componentIds.forEach((componentId) => {
        if (layoutFlights.current.get(componentId) === next) {
          layoutFlights.current.delete(componentId);
        }
      });
    });
  };

  const awaitLayoutSave = async (componentId: number): Promise<void> => {
    const flight = layoutFlights.current.get(componentId);
    if (flight) {
      await flight;
    }
  };

  const getVersion = async () : Promise<void> => {
    if (contentSvcRef.current) {
      const ver = await contentSvcRef.current.getVersion(Number(versionid));

      if (ver) {
        setVersionDetail(ver);
        await getSections(ver.Id);
      }
    }
  };

  const getSections = async (versionid: number) : Promise<void> => {
    if (!contentSvcRef.current) {
      return;
    }

    const started = layoutClock.current;
    const flights = Array.from(layoutFlights.current.values());
    await Promise.all(flights);
    const sectionsres = await contentSvcRef.current.getVersionSections(versionid);

    commitSections((current) => mergeFetchedSections(
      sectionsres ?? [],
      current,
      started,
      componentRevisions.current,
      removedComponentGenerations.current,
      removedSectionGenerations.current
    ));
  };

  const getComponents = async () : Promise<void> => {
    if (contentSvcRef.current) {
      const res = await contentSvcRef.current.getComponents();

      if (res?.length) {
        setComponents(res);
      }
    }
  };

  const getLayoutTemplates = async () : Promise<void> => {
    const templates = await contentSvcRef.current?.getLayoutTemplates();

    if (templates?.length) {
      setLayoutTemplates(templates);
    }
  };

  const getScriptsAndStyles = async (): Promise<void> => {
    if (contentEditSvcRef.current) {
      const styleres = await contentEditSvcRef.current.GetScriptsAndCss(ScriptIncludeType.Css);

      if (styleres) {
        const enabledstyles = styleres.filter(s => s.Enabled && s.Code);

        if (enabledstyles?.length > 0) {
          enabledstyles.forEach(es => {
            if (es.Code !== null && es.Code.length > 0) {
              parseCSS(es.Code);
            }
          });
        }
      }
    }
  };

  const parseCSS = (cssString: string): void => {
    // Create a detached document fragment (not attached to the main document)
    let tempdoc: Document | null = document.implementation.createHTMLDocument("");
    const styleElement = document.createElement("style");
    styleElement.textContent = cssString;
    tempdoc.body.appendChild(styleElement);

    let cssRules = null;

    // Once the style is in the fragment, the sheet gets populated
    if (styleElement.sheet && styleElement.sheet?.cssRules?.length > 0) {
      cssRules = Array.from(styleElement.sheet.cssRules); // Convert CSSRuleList to array
      const styles = cssRules?.map(cr => { return cr as CSSStyleRule });
      styleElement.remove();
      tempdoc = null;

      if (styles.length > 0) {
        setSiteStyles((prev) => [
          ...prev,
          ...styles
        ]);
      }
    }
  };

  const openSectionWizard = (currentorder: number, above: boolean): void => {
    const order = above ? currentorder : currentorder + 1;
    setPendingSectionOrder(order);
    setAddAbove(above);

    if (!aiAvailable) {
      addManualSection(order);
      return;
    }

    setShowSectionWizard(true);
  };

  const openSectionWizardForSection = (sectionId: number, above: boolean): void => {
    const order = sectionInsertionOrder(sectionsRef.current, sectionId, above);
    if (order !== null) openSectionWizard(above ? order : order - 1, above);
  };

  const addManualSection = (orderOverride?: number): void => {
    if (versionDetail) {
      const newsection: IVersionSection = {
        BackgroundColour: undefined,
        BackgroundImage: undefined,
        ClientScript: undefined,
        Columns: 12,
        ContentComponents: [],
        DynamicComponents: undefined,
        ExtendedProperties: undefined,
        Id: 0,
        IsPreview: false,
        JSFilePath: undefined,
        Name: undefined,
        Order: orderOverride ?? pendingSectionOrder,
        PageVersion: versionDetail,
        SectionClass: undefined,
        SectionHRefId: undefined,
        PageVersionId: versionDetail.Id,
        Template: TemplateType.ColumnsCC
      };

      setSelectedSection(newsection);
      setShowSectionOptions(true);
      setShowSectionWizard(false);
    }
  };

  const createAISection = async (brief: IAISectionCreationBrief): Promise<void> => {
    if (!versionDetail || !aiGenerationSvcRef.current) {
      return;
    }

    setShowGenerationOverlay(true);
    setGenerationMessage("Generating section");

    try {
      await aiGenerationSvcRef.current.generateSingleSectionFromBrief(versionDetail, pendingSectionOrder, brief, (message) => {
        setGenerationMessage(message);
      });

      await getSections(versionDetail.Id);
      setShowSectionWizard(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "AI section generation failed.";
      actionEmitter.emit(EmitEvent.Result, false, message);
    } finally {
      setShowGenerationOverlay(false);
    }
  };

  const editSection = (sectionid: number): void => {
    const section: IVersionSection | undefined = sections.find(s => s.Id === sectionid);

    if (section) {
      setSelectedSection(section);
      setShowSectionOptions(true);
    }
  };

  const moveSection = async (sectionId: number, direction: "up" | "down"): Promise<void> => {
    if (!contentEditSvcRef.current || !versionDetail) {
      return;
    }

    const sortedSections = [...sectionsRef.current].sort((a, b) => a.Order - b.Order);
    const currentIndex = sortedSections.findIndex((section) => section.Id === sectionId);

    if (currentIndex < 0) {
      return;
    }

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= sortedSections.length) {
      return;
    }

    if (sectionMoveInFlight.current) return;
    sectionMoveInFlight.current = true;
    setMovingSection(true);
    try {
      const moved = await contentEditSvcRef.current.ReIndexSection(sectionId, targetIndex + 1);
      if (moved) await getSections(versionDetail.Id);
    } catch {
      actionEmitter.emit(EmitEvent.Result, false, "Unable to move section. Please try again.");
    } finally {
      sectionMoveInFlight.current = false;
      setMovingSection(false);
    }
  };

  const deleteSection = async (sectionid: number, confirmedbypass: boolean): Promise<void> => {
    const section: IVersionSection | undefined = sections.find(s => s.Id === sectionid);

    if (section && contentEditSvcRef.current) {

      const confirmed = confirmedbypass || await requestConfirmation("Are you sure you want to remove this section? All content in this section will be removed.", "Remove Section");

      if (confirmed) {
        const removed = await contentEditSvcRef.current.DeleteSection(sectionid);

        if (removed) {
          removedSectionGenerations.current.set(sectionid, ++layoutClock.current);
          section.ContentComponents?.forEach((component) => noteComponentRemoved(component.Id));
          setSelectedSection((current) => current?.Id === sectionid ? null : current);
          commitSections((current) => reindexSections(current.filter((item) => item.Id !== sectionid)));
          await getSections(section.PageVersionId);
        }
      }
    }
  };

  const saveSection = async (section: IVersionSection): Promise<void> => {
    if (section && contentEditSvcRef.current) {
      const isNewSection = section.Id === 0;

      const res = await contentEditSvcRef.current.SaveSection(section);

      if (res) {
        if (isNewSection) {
          await getSections(section.PageVersionId);
        } else {
          commitSections((current) => {
            const existing = current.find((item) => item.Id === res.Id);

            if (!existing) {
              return [...current, { ...res, ContentComponents: res.ContentComponents ?? [] }];
            }

            return current.map((item) => item.Id === res.Id
              ? { ...res, ContentComponents: existing.ContentComponents ?? [] }
              : item);
          });
        }

        setSelectedSection(null);
        setShowSectionOptions(false);
      }
    }
  };

  const getSectionContainer = (section: IVersionSection): string => {
    let cont: string = "container";

    if (section.Template?.includes("FullWidth")) {
      cont = "container-fluid m-0 p-0";
    }
    return cont;
  };

  const getSectionBackgroundStyle = (section: IVersionSection): React.CSSProperties => {
    const style: React.CSSProperties = {};

    if (section.BackgroundColour !== undefined && section.BackgroundColour?.length > 0) {
      style.backgroundColor = section.BackgroundColour;
    }

    if (section.BackgroundImage !== undefined && section.BackgroundImage?.length > 0) {
      style.backgroundImage = `url("${section.BackgroundImage}")`;
    }

    return style;
  };

  const getSectionStyle = (section: IVersionSection): {} => {
    const style = getSectionBackgroundStyle(section);
    if (section.BackgroundImage !== undefined && section.BackgroundImage?.length > 0) {
      style.opacity = 1;
    }
    setSectionClassesAsStyle(section);
    return style;
  };

  const setSectionClassesAsStyle = (section: IVersionSection): void => {
    if (siteStyles.length > 0) {
      const classes = section.SectionClass?.split(" ");

      if (classes && classes.length > 0) {
        const container = document.getElementById(`section-container-${section.Id}`);
        let styletext: string = "";

        if (container) {
          classes.forEach(cls => {
            let style = siteStyles.find(ss => ss.selectorText === `.${cls}`);

            if (style) {
              styletext += style.cssText;
            }
          });
        }

        if (styletext) {
          const styleelem = document.createElement("style");
          styleelem.innerText = styletext;
          container?.appendChild(styleelem);
        }
      }
    }
  };

  const onComponentEdit = (component: IContentComponent, propertyName?: string): void => {
    if (component && inlineDirtyComponentsRef.current.has(component.Id)) {
      actionEmitter.emit(EmitEvent.Result, false, "Save or discard this component's inline changes before opening its properties.");
      return;
    }
    if (nestedComponentEditorRef.current) {
      actionEmitter.emit(EmitEvent.Result, false, "Save or cancel the current item before selecting another component.");
      return;
    }
    setSelectedProperty(propertyName);
    if (component) {
      setSelectedContentComponent(component);
    } else {
      setSelectedContentComponent(null);
    }
  };

  const duplicateComponent = async (component: IContentComponent): Promise<void> => {
    if (!contentEditSvcRef.current || duplicatingComponentRef.current) return;
    if (componentDrafts[component.Id] || inlineDirtyComponentsRef.current.has(component.Id) || nestedComponentEditorRef.current) {
      actionEmitter.emit(EmitEvent.Result, false, "Save or discard your edits before duplicating this component.");
      return;
    }
    duplicatingComponentRef.current = true;
    setDuplicatingComponentId(component.Id);
    try {
      await awaitLayoutSave(component.Id);
      let section = sectionsRef.current.find(item => item.Id === component.SectionId);
      const live = section?.ContentComponents?.find(item => item.Id === component.Id);
      if (!section || !live) return;
      const source = componentCopySource(live);
      const columns = section.Template === TemplateType.ColumnsCC || section.Template === TemplateType.ColumnsFW;
      let column = columns ? emptyCopyColumn(section, live) : undefined;
      if (columns && column === undefined) {
        const created = await contentEditSvcRef.current.SaveSection({ ...section, Id: 0, Name: undefined,
          ContentComponents: [], Order: Math.max(0, ...sectionsRef.current.map(item => item.Order)) + 1 });
        if (!created) return;
        section = { ...created, ContentComponents: [] };
        commitSections(current => current.some(item => item.Id === created.Id) ? current : [...current, section!]);
        column = 0;
      }
      const position = copyGridPosition(section, live);
      let copy = columns
        ? await contentEditSvcRef.current.AddCComponentToColumnSection(source, section.Id, column!)
        : await contentEditSvcRef.current.AddComponentToGridSection(section.Id, source, position.x, position.y, position.w, position.h);
      if (!copy) return;
      if (columns && copy.ColSpan !== live.ColSpan) {
        const resized = await contentEditSvcRef.current.SaveContentComponent({ ...copy, ColSpan: live.ColSpan });
        if (resized) copy = resized;
        else actionEmitter.emit(EmitEvent.Result, false, "The copy was created, but its column span could not be restored. Check its properties.");
      }
      const createdCopy = copy;
      changeSectionComponents(section.Id, components => components.some(item => item.Id === createdCopy.Id) ? components : [...components, createdCopy]);
      onComponentEdit(copy);
      actionEmitter.emit(EmitEvent.Result, true, columns && column === 0 && section.Id !== live.SectionId
        ? "Component duplicated into a new section." : "Component duplicated.");
    } catch {
      actionEmitter.emit(EmitEvent.Result, false, "Unable to duplicate this component. Please try again.");
    } finally {
      duplicatingComponentRef.current = false;
      setDuplicatingComponentId(undefined);
    }
  };

  const onComponentSave = async (component: IContentComponent): Promise<boolean> => {
    if (!contentEditSvcRef.current) {
      return false;
    }

    await awaitLayoutSave(component.Id);

    const live = sectionsRef.current
      .find((section) => section.Id === component.SectionId)
      ?.ContentComponents
      ?.find((item) => item.Id === component.Id);
    const toSave = withLiveLayout(component, live);
    const res = await contentEditSvcRef.current.SaveContentComponent(toSave);

    if (!res) {
      return false;
    }

    if (removedComponentGenerations.current.has(res.Id)) {
      setSelectedContentComponent(null);
      return false;
    }

    const liveAfter = sectionsRef.current
      .find((section) => section.Id === res.SectionId)
      ?.ContentComponents
      ?.find((item) => item.Id === res.Id);

    changeSectionComponents(res.SectionId, (components) => {
      const existing = components.find((item) => item.Id === res.Id);
      const merged = withLiveLayout(res, existing ?? liveAfter);

      return existing
        ? components.map((item) => item.Id === res.Id ? merged : item)
        : [...components, merged];
    });

    if (liveAfter && !layoutsMatch(toSave, liveAfter)) {
      const gridChanged = toSave.PosX !== liveAfter.PosX
        || toSave.PosY !== liveAfter.PosY
        || toSave.Cols !== liveAfter.Cols
        || toSave.Rows !== liveAfter.Rows;

      if (gridChanged) {
        enqueueLayoutSave([res.Id], () => contentEditSvcRef.current!.MoveGridComponent(
          res.SectionId,
          res.Id,
          liveAfter.x ?? liveAfter.PosX,
          liveAfter.y ?? liveAfter.PosY,
          liveAfter.w ?? liveAfter.Cols,
          liveAfter.h ?? liveAfter.Rows
        ));
      } else if (toSave.ColIndex !== liveAfter.ColIndex) {
        enqueueLayoutSave([res.Id], () => contentEditSvcRef.current!.MoveContentComponent(res.Id, liveAfter.ColIndex));
      }
    }

    setComponentDrafts(drafts => { const next = { ...drafts }; delete next[res.Id]; return next; });
    templateRefs.current[`SEC-${res.SectionId}`]?.refreshComponent(res);
    setSelectedContentComponent(selected => selected?.Id === res.Id ? null : selected);
    return true;
  };

  const setupIframeEventHandlers = () => {
    const handleMessage = (evt: MessageEvent) => {
      if (evt && evt.data && evt.data.compId) {
        const handler = iframeMessageHandlers.get(evt.data.compId);
        if (handler) {
          handler(evt.data);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  };

  const publishVersion = async () => {
    if (Object.keys(componentDrafts).length || inlineDirtyComponentsRef.current.size || nestedComponentEditorRef.current) {
      actionEmitter.emit(EmitEvent.Result, false, "Save or discard your component edits before publishing this version.");
      return;
    }
    if (contentSvcRef.current && versionDetail) {
      const res = await contentSvcRef.current.publishVersion(versionDetail.Id);
      if (res) {
        setVersionDetail(res);
      }
      actionEmitter.emit(EmitEvent.Result, res !== null, res ? "Version published" : "Failed to publish version");
    }
  };

  const unPublishVersion = async () => {
    if (contentSvcRef.current && versionDetail) {
      const res = await contentSvcRef.current.unPublishVersion(versionDetail.Id);
      if (res) {
        setVersionDetail(res);
      }
      actionEmitter.emit(EmitEvent.Result, res !== null, res ? "Version unpublished" : "Failed to unpublish version");
    }
  };

  const deleteAllSections = async () => {
    if (sections && sections.length > 0 && contentEditSvcRef.current) {
      const res = await requestConfirmation("Are you sure you want to remove all sections? This can't be undone", "Delete Sections");

      if (res) {
        const sectionIds = sectionsRef.current.map((sec) => sec.Id);
        // Each deletion reindexes the version on the server; keep those writes sequential.
        const deleteResults: boolean[] = [];
        for (const sectionId of sectionIds) {
          try {
            deleteResults.push(await contentEditSvcRef.current.DeleteSection(sectionId));
          } catch {
            deleteResults.push(false);
          }
        }
        const failedIds = new Set(sectionIds.filter((_, index) => !deleteResults[index]));

        sectionIds.forEach((sectionId, index) => {
          if (deleteResults[index]) {
            sectionsRef.current.find((section) => section.Id === sectionId)?.ContentComponents
              ?.forEach((component) => noteComponentRemoved(component.Id));
            removedSectionGenerations.current.set(sectionId, ++layoutClock.current);
          }
        });

        commitSections((current) => reindexSections(current.filter((section) => !sectionIds.includes(section.Id) || failedIds.has(section.Id))));
        setSelectedSection((current) => current && sectionIds.includes(current.Id) && !failedIds.has(current.Id) ? null : current);
        sectionIds.filter((id) => !failedIds.has(id)).forEach((id) => { delete templateRefs.current[id]; });
        if (failedIds.size > 0) {
          actionEmitter.emit(EmitEvent.Result, false, "Some sections could not be deleted. Their content and editors have been kept; please try again.");
        }
      }
    }
  };

  useEffect(() => {
    if (!siteSwitchRef.current) {
      siteSwitchRef.current = new SiteSwitcher();
    }

    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }

    if (!contentEditSvcRef.current) {
      contentEditSvcRef.current = new ContentEditorService();
    }


    if (!aiGenerationSvcRef.current) {
      aiGenerationSvcRef.current = new AIContentGenerationService();
    }

    if (!siteSvcRef.current) {
      siteSvcRef.current = new SiteService();
    }

    const initdata = async () => {
      try {
        const siteDetails = await siteSvcRef.current?.getSiteDetails();
        setPreviewDomain(siteDetails?.Domain ?? null);
        setAiAvailable(isSiteAiConnected(siteDetails));

        await Promise.all([
          getComponents(),
          getLayoutTemplates(),
          getVersion(),
          getScriptsAndStyles(),
        ]);

        setIsLoaded(true);
      } catch (error) {
        console.error("Error loading data:", error);
      }
      setupIframeEventHandlers();
    };

    initdata();

  }, []);

  useEffect(() => {
    if (selectedSection && showSectionOptions) {
      console.log("Showing section details");
    }
  }, [selectedSection, showSectionOptions]);

  useEffect(() => {
    if (showPageSettings && versionDetail) {
      const draft: IVersion = {
        ...versionDetail,
        PageBackgroundColour: versionDetail.PageBackgroundColour ?? "",
        PageBackgroundImage: versionDetail.PageBackgroundImage ?? "",
        PageBackgroundImageStyle: versionDetail.PageBackgroundImageStyle ?? "cover",
        PageBackgroundImagePosition: versionDetail.PageBackgroundImagePosition ?? "center center",
        PageBackgroundImageRepeat: versionDetail.PageBackgroundImageRepeat ?? "no-repeat",
        PageBackgroundImageAttachment: versionDetail.PageBackgroundImageAttachment ?? "scroll",
        PageBackgroundOverlayColour: versionDetail.PageBackgroundOverlayColour ?? "",
        PageBackgroundOverlayOpacity: versionDetail.PageBackgroundOverlayOpacity ?? null,
        PageClass: versionDetail.PageClass ?? "",
        PageStyle: versionDetail.PageStyle ?? "",
        PageScript: versionDetail.PageScript ?? "",
      };

      setPageSettingsDraft(draft);
      setPageBackgroundType(getBackgroundType(draft));
    }
  }, [showPageSettings, versionDetail]);

  const updatePageSettingsDraft = <K extends keyof IVersion>(field: K, value: IVersion[K]): void => {
    setPageSettingsDraft((prev) => prev ? { ...prev, [field]: value } : prev);
  };

  const onBackgroundTypeChange = (nextType: string): void => {
    setPageBackgroundType(nextType);
    setPageSettingsDraft((prev) => {
      if (!prev) {
        return prev;
      }

      const nextDraft: IVersion = { ...prev };

      if (nextType === "none" || nextType === "image") {
        nextDraft.PageBackgroundColour = "";
      }

      if (nextType === "none" || nextType === "colour") {
        nextDraft.PageBackgroundImage = "";
        nextDraft.PageBackgroundImageStyle = nextDraft.PageBackgroundImageStyle ?? "cover";
        nextDraft.PageBackgroundImagePosition = nextDraft.PageBackgroundImagePosition ?? "center center";
        nextDraft.PageBackgroundImageRepeat = nextDraft.PageBackgroundImageRepeat ?? "no-repeat";
        nextDraft.PageBackgroundImageAttachment = nextDraft.PageBackgroundImageAttachment ?? "scroll";
        nextDraft.PageBackgroundOverlayColour = "";
        nextDraft.PageBackgroundOverlayOpacity = null;
      }

      return nextDraft;
    });
  };

  const savePageSettings = async (): Promise<void> => {
    if (contentSvcRef.current && pageSettingsDraft) {
      const savedVersion = await contentSvcRef.current.saveVersion(pageSettingsDraft);

      if (savedVersion) {
        const mergedVersion = mergeVersionPresentationSettings(savedVersion, pageSettingsDraft);
        setVersionDetail(mergedVersion);
        setPageSettingsDraft(mergedVersion);
        setShowPageSettings(false);
      }
    }
  };

  const effectiveVersion = pageSettingsDraft ?? versionDetail;
  useEffect(() => {
    if (!Object.keys(componentDrafts).length) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [componentDrafts]);

  const editedSection = selectedContentComponent
    ? sections.find((section) => section.Id === selectedContentComponent.SectionId)
    : undefined;
  const componentEditorFrame = selectedContentComponent
    ? buildPageSectionFrame({
        pageClass: effectiveVersion?.PageClass,
        pageStyle: getVersionCanvasStyle(effectiveVersion),
        pageCss: effectiveVersion?.PageStyle,
        sectionClass: editedSection?.SectionClass,
        sectionId: editedSection
          ? (editedSection.SectionHRefId?.trim() || String(editedSection.Id))
          : "",
        sectionStyle: editedSection ? getSectionBackgroundStyle(editedSection) : undefined
      })
    : undefined;

  return (
    <div id="designer-content" className={styles.designerPage}>
      {isLoaded && (
        <div className={styles.designerPageLoaded}>
          <div id="DragFileIcon" hidden={true}>
            <FontAwesomeIcon icon={faFile} size="2x" style={{ maxWidth: '30px', maxHeight: '30px' }} />
          </div>

          <div className={styles.designerLayout} key="DESIGNER-CONTENT">

              {/* Persistent component sidebar */}
              {components?.length > 0 && (
                <ComponentSidebar components={components} />
              )}

              {/* Canvas */}
              <div className={styles.designerCanvas}>
              {sections?.length > 0 && components?.length > 0 && (
                <ComponentToolbar
                  key={"MAIN-TOOLBAR"}
                  onAddSection={openSectionWizard}
                  onOpenPageSettings={() => setShowPageSettings(true)}
                  onPublishVersion={publishVersion}
                  onUnPublishVersion={unPublishVersion}
                  onDeleteAllSections={deleteAllSections}
                  SectionCount={sections?.length ?? 0}
                  VersionPreviewUrl={resolvedPreviewUrl}
                  VersionPublished={versionDetail?.Status === VersionStatus.Published }
                  layoutPreviewOpen={layoutPreviewMode !== null}
                  onToggleLayoutPreview={() => setLayoutPreviewMode((mode) => mode ? null : "desktop")} />
              )}
              {Object.keys(componentDrafts).length > 0 && <div className="d-flex flex-wrap align-items-center gap-2 px-3 py-2" aria-label="Component drafts">
                <span className="small">Unsaved components:</span>
                {Object.values(componentDrafts).map(draft => <Button key={draft.Id} size="sm" variant="outline-secondary"
                  onClick={() => { const live = sectionsRef.current.flatMap(section => section.ContentComponents ?? []).find(component => component.Id === draft.Id); if (live) onComponentEdit(live); }}>
                  {draft.DisplayName} #{draft.Id}
                </Button>)}
              </div>}
              {layoutPreviewMode && (
                <LayoutPreview
                  previewUrl={resolvedPreviewUrl}
                  mode={layoutPreviewMode}
                  onModeChange={setLayoutPreviewMode}
                  onClose={() => setLayoutPreviewMode(null)}
                />
              )}
              <div
                id="page-version-canvas"
                className={`${styles.versionCanvas} ${effectiveVersion?.PageClass ?? ""}`.trim()}
                hidden={layoutPreviewMode !== null}
                style={{ ...getVersionCanvasStyle(effectiveVersion), ...(layoutPreviewMode ? { display: "none" } : {}) }}>
                {effectiveVersion?.PageStyle && (
                  <style>{effectiveVersion.PageStyle}</style>
                )}

                {sections?.length ? (
                  [...sections]
                    .sort((a, b) => a.Order - b.Order)
                    .map((sec: IVersionSection, sectionIndex: number) => (
                      <section
                        key={`section-${sec.Id}`}
                        id={`section-container-${sec.Id}`}
                        style={{ ...getSectionStyle(sec) }}
                        className={`${styles.sectionFrame} ${sec.SectionClass ?? ""}`.trim()}>

                        <div className={`${getSectionContainer(sec)}`} id={`section-${sec.Id}`}>
                          <div className={styles.sectionContainer}>
                            <div className={styles.hoverzonetop}></div>

                            {(sec.Template === TemplateType.ColumnsCC || sec.Template === TemplateType.ColumnsFW) && (
                              <ColumnsTemplate
                                ref={el => (templateRefs.current[`SEC-${sec.Id}`] = el)}
                                Columns={sec.Columns}
                                ContentComponents={sec.ContentComponents}
                                VersionId={sec.PageVersionId}
                                SectionId={sec.Id}
                                PageClass={effectiveVersion?.PageClass ?? ""}
                                SectionClass={sec.SectionClass ?? ""}
                                SectionAnchor={sec.SectionHRefId ?? ""}
                                PageStyle={getVersionCanvasStyle(effectiveVersion)}
                                SectionStyle={getSectionBackgroundStyle(sec)}
                                EditComponent={onComponentEdit}
                                EditingComponentId={selectedContentComponent?.Id}
                                DraftComponentIds={Object.keys(componentDrafts).map(Number)}
                                ComponentDrafts={componentDrafts}
                                DuplicateComponent={duplicateComponent}
                                DuplicatingComponentId={duplicatingComponentId}
                                OnInlineDirtyChange={onInlineDirtyChange}
                                SaveInlineComponent={onComponentSave}
                                PageCss={effectiveVersion?.PageStyle ?? ""}
                                onComponentsChange={changeSectionComponents}
                                enqueueLayoutSave={enqueueLayoutSave} />
                            )}

                            {(sec.Template === TemplateType.ContentCC || sec.Template === TemplateType.ContentFW) && (
                              <GridStackTemplate
                                ref={el => (templateRefs.current[`SEC-${sec.Id}`] = el)}
                                ContentComponents={sec.ContentComponents}
                                AvailableComponents={components}
                                SectionId={sec.Id}
                                VersionId={sec.PageVersionId}
                                PageClass={effectiveVersion?.PageClass ?? ""}
                                SectionClass={sec.SectionClass ?? ""}
                                SectionAnchor={sec.SectionHRefId ?? ""}
                                PageStyle={getVersionCanvasStyle(effectiveVersion)}
                                SectionStyle={getSectionBackgroundStyle(sec)}
                                EditComponent={onComponentEdit}
                                EditingComponentId={selectedContentComponent?.Id}
                                DraftComponentIds={Object.keys(componentDrafts).map(Number)}
                                ComponentDrafts={componentDrafts}
                                DuplicateComponent={duplicateComponent}
                                DuplicatingComponentId={duplicatingComponentId}
                                OnInlineDirtyChange={onInlineDirtyChange}
                                SaveInlineComponent={onComponentSave}
                                PageCss={effectiveVersion?.PageStyle ?? ""}
                                onComponentsChange={changeSectionComponents}
                                enqueueLayoutSave={enqueueLayoutSave} />
                            )}

                            <div className={styles.hoverzonebottom}></div>

                            <div className={styles.sectionControls}>
                              <div className={styles.sectionControlsTitle}>
                                <span className={styles.sectionControlsLabel}>Section</span>
                              </div>
                              <div className={styles.sectionControlsActions}>
                                <div className={styles.sectionControlsColumn}>
                                  <Button
                                    variant="link"
                                    className={styles.sectionControlButton}
                                    onClick={() => openSectionWizardForSection(sec.Id, true)}
                                    title="Add Section Above">
                                    <FontAwesomeIcon icon={faPlus} size="sm" />
                                  </Button>
                                  <Button
                                    variant="link"
                                    className={styles.sectionControlButton}
                                    disabled={movingSection || sectionIndex === 0}
                                    onClick={() => moveSection(sec.Id, "up")}
                                    title="Move Section Up">
                                    <FontAwesomeIcon icon={faArrowUp} size="sm" />
                                  </Button>
                                  <Button
                                    variant="link"
                                    className={styles.sectionControlButton}
                                    disabled={movingSection || sectionIndex === sections.length - 1}
                                    onClick={() => moveSection(sec.Id, "down")}
                                    title="Move Section Down">
                                    <FontAwesomeIcon icon={faArrowDown} size="sm" />
                                  </Button>
                                  <Button
                                    variant="link"
                                    className={styles.sectionControlButton}
                                    onClick={() => openSectionWizardForSection(sec.Id, false)}
                                    title="Add Section Below">
                                    <FontAwesomeIcon icon={faPlus} size="sm" />
                                  </Button>
                                </div>
                                <div className={styles.sectionControlsColumn}>
                                  <Button
                                    variant="link"
                                    className={styles.sectionControlButton}
                                    onClick={() => editSection(sec.Id)}
                                    title="Edit Section Properties">
                                    <FontAwesomeIcon icon={faEdit} size="sm" />
                                  </Button>
                                  <Button
                                    variant="link"
                                    className={styles.sectionControlButton}
                                    onClick={() => deleteSection(sec.Id, false)}
                                    title="Delete This Section">
                                    <FontAwesomeIcon icon={faTrash} size="sm" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>
                    ))
                ) : (
                  <Row className="mb-3" key="NO-SECTIONS-MESSAGE">
                    <Col xs={12} className={styles.emptySectionsWrap}>
                      <div className={styles.emptySectionsCard}>
                        <AppEmptyState
                          compact={true}
                          className={`${styles.centeredEmptyState} justify-content-center`}
                          title="No content sections found"
                          message="Create your first section to start building this page layout."
                        />
                        <Button variant="outline-secondary"
                          type="button"
                          className="btn-nomin mt-3"
                          title="add new section"
                          onClick={() => openSectionWizard(0, true)}>
                          <FontAwesomeIcon icon={faPlus} className="me-1" />
                          <span>add new content section</span>
                        </Button>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
              </div>
              {selectedContentComponent && <ContentComponentEditor
                key={`COMPONENT-EDITOR-${selectedContentComponent.Id}`}
                ContentComponent={componentDrafts[selectedContentComponent.Id] ?? selectedContentComponent}
                savedComponent={selectedContentComponent}
                presentation="panel"
                initiallyExpanded
                focusProperty={selectedProperty}
                onNestedEditorChange={onNestedEditorChange}
                PageLanguage={versionDetail?.Page?.Language}
                editorFrame={componentEditorFrame}
                onDraftChange={draft => setComponentDrafts(previous => {
                  const next = { ...previous };
                  if (JSON.stringify(draft) === JSON.stringify(selectedContentComponent)) delete next[draft.Id];
                  else next[draft.Id] = draft;
                  return next;
                })}
                onDiscard={() => setComponentDrafts(previous => { const next = { ...previous }; delete next[selectedContentComponent.Id]; return next; })}
                onClose={() => setSelectedContentComponent(null)}
                isOpen={true}
                onSaveComponent={onComponentSave} />}
            </div>
        </div>
      )}

      {!isLoaded && (
        <Loading Message="Loading page content..." SpinSize="6x" key={"CONTENT-LOADING"} />
      )}

      {selectedSection && (
        <SectionAddEdit
          key="SECTION-ADDEDIT"
          isNew={selectedSection.Id === 0}
          isOpen={showSectionOptions}
          onClose={() => { setSelectedSection(null); setShowSectionOptions(false); }}
          onSubmit={saveSection}
          section={selectedSection}
          above={addAbove}
        />
      )}

      {showSectionWizard && (
        <AppModal
          onClose={() => setShowSectionWizard(false)}
          showButtons={false}
          size="lg"
          title="Create Section">
          <NewSectionWizard
            aiAvailable={aiAvailable}
            onCancel={() => setShowSectionWizard(false)}
            onManualSectionCreated={addManualSection}
            onAISectionCreated={createAISection}
          />
        </AppModal>
      )}

      {showGenerationOverlay && (
        <AppModal
          onClose={() => undefined}
          showButtons={false}
          size="md"
          title="Generating Content"
          backdrop="static">
          <Loading SpinSize="3x" Message={generationMessage} />
        </AppModal>
      )}

      {showPageSettings && (
        <AppModal
          onClose={() => setShowPageSettings(false)}
          showButtons={true}
          size="xl"
          onDataAction={savePageSettings}
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Close"
          dataActionButtonText="Save Changes"
          title="Page Settings"
          enableActionButton={pageSettingsDraft !== null}
        >
          {pageSettingsDraft && (
            <div className={styles.pageSettingsMock}>
              <div className={styles.pageSettingsIntro}>
                <h3 className={styles.pageSettingsTitle}>Version-wide page settings</h3>
                <p className={styles.pageSettingsText}>
                  Presentation settings apply to the full page version first. Section backgrounds, classes, and styles will still take over inside each section where they are defined.
                </p>
              </div>

              <Tab.Container defaultActiveKey="presentation">
                <Nav variant="tabs" className={styles.pageSettingsTabs}>
                  <Nav.Item>
                    <Nav.Link eventKey="presentation">Presentation</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="scripts">Scripts</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="styles">Styles</Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content className={styles.pageSettingsTabContent}>
                  <Tab.Pane eventKey="presentation">
                    <Row>
                      <Col md={6} className="mt-3">
                        <FormLabel>Background Type</FormLabel>
                        <FormSelect value={pageBackgroundType} onChange={(e) => onBackgroundTypeChange(e.target.value)}>
                          <option value="none">None</option>
                          <option value="colour">Solid colour</option>
                          <option value="image">Image</option>
                          <option value="colour-image">Colour + image</option>
                        </FormSelect>
                      </Col>
                      <Col md={6} className="mt-3">
                        <FormLabel>Page Class</FormLabel>
                        <FormControl
                          type="text"
                          value={pageSettingsDraft.PageClass ?? ""}
                          onChange={(e) => updatePageSettingsDraft("PageClass", e.target.value)}
                          placeholder="e.g. landing-page hero-theme"
                        />
                      </Col>
                    </Row>

                    {(pageBackgroundType === "colour" || pageBackgroundType === "colour-image") && (
                      <Row>
                        <Col md={6} className="mt-3">
                          <FormLabel>Page Background Colour</FormLabel>
                          <ColourPicker
                            selectedColour={pageSettingsDraft.PageBackgroundColour ?? ""}
                            onColourChange={(colour) => updatePageSettingsDraft("PageBackgroundColour", colour ?? "")}
                            name="PageBackgroundColour"
                            id="PageBackgroundColour"
                          />
                        </Col>
                      </Row>
                    )}

                    {(pageBackgroundType === "image" || pageBackgroundType === "colour-image") && (
                      <>
                        <Row>
                          <Col md={12} className="mt-3">
                            <SiteMediaPicker
                              existingUrl={pageSettingsDraft.PageBackgroundImage ?? ""}
                              onMediaSelect={(url) => updatePageSettingsDraft("PageBackgroundImage", url)}
                              onMediaClear={() => updatePageSettingsDraft("PageBackgroundImage", "")}
                              captionLabel="Page Background Image"
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4} className="mt-3">
                            <FormLabel>Image Fit</FormLabel>
                            <FormSelect
                              value={pageSettingsDraft.PageBackgroundImageStyle ?? "cover"}
                              onChange={(e) => updatePageSettingsDraft("PageBackgroundImageStyle", e.target.value)}>
                              <option value="cover">Cover</option>
                              <option value="contain">Contain</option>
                              <option value="auto">Auto</option>
                            </FormSelect>
                          </Col>
                          <Col md={4} className="mt-3">
                            <FormLabel>Image Position</FormLabel>
                            <FormSelect
                              value={pageSettingsDraft.PageBackgroundImagePosition ?? "center center"}
                              onChange={(e) => updatePageSettingsDraft("PageBackgroundImagePosition", e.target.value)}>
                              <option value="center center">Center</option>
                              <option value="top center">Top</option>
                              <option value="bottom center">Bottom</option>
                              <option value="center left">Left</option>
                              <option value="center right">Right</option>
                            </FormSelect>
                          </Col>
                          <Col md={4} className="mt-3">
                            <FormLabel>Image Repeat</FormLabel>
                            <FormSelect
                              value={pageSettingsDraft.PageBackgroundImageRepeat ?? "no-repeat"}
                              onChange={(e) => updatePageSettingsDraft("PageBackgroundImageRepeat", e.target.value)}>
                              <option value="no-repeat">No repeat</option>
                              <option value="repeat">Repeat</option>
                              <option value="repeat-x">Repeat x</option>
                              <option value="repeat-y">Repeat y</option>
                            </FormSelect>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4} className="mt-3">
                            <FormLabel>Image Attachment</FormLabel>
                            <FormSelect
                              value={pageSettingsDraft.PageBackgroundImageAttachment ?? "scroll"}
                              onChange={(e) => updatePageSettingsDraft("PageBackgroundImageAttachment", e.target.value)}>
                              <option value="scroll">Scroll</option>
                              <option value="fixed">Fixed</option>
                              <option value="local">Local</option>
                            </FormSelect>
                          </Col>
                          <Col md={4} className="mt-3">
                            <FormLabel>Overlay Colour</FormLabel>
                            <ColourPicker
                              selectedColour={pageSettingsDraft.PageBackgroundOverlayColour ?? ""}
                              onColourChange={(colour) => updatePageSettingsDraft("PageBackgroundOverlayColour", colour ?? "")}
                              name="PageBackgroundOverlayColour"
                              id="PageBackgroundOverlayColour"
                            />
                          </Col>
                          <Col md={4} className="mt-3">
                            <FormLabel>Overlay Opacity</FormLabel>
                            <div className="d-flex align-items-center gap-3">
                              <FormControl
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                className="flex-grow-1"
                                value={Math.round((pageSettingsDraft.PageBackgroundOverlayOpacity ?? 0) * 100)}
                                onChange={(e) => updatePageSettingsDraft("PageBackgroundOverlayOpacity", Number(e.target.value) / 100)}
                              />
                              <span className={styles.pageSettingsSliderValue}>
                                {Math.round((pageSettingsDraft.PageBackgroundOverlayOpacity ?? 0) * 100)}%
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row>
                      <Col md={6} className="mt-3">
                        <FormCheck
                          type="switch"
                          id="page-settings-breadcrumbs"
                          label="Display breadcrumbs"
                          checked={pageSettingsDraft.DisplayBreadcrumbs}
                          onChange={(e) => updatePageSettingsDraft("DisplayBreadcrumbs", e.target.checked)}
                        />
                      </Col>
                    </Row>

                    <div className={styles.pageSettingsPreview}>
                      <div className={styles.pageSettingsPreviewLabel}>Preview Summary</div>
                      <div className={styles.pageSettingsPreviewValue}>
                        {pageBackgroundType === "none" && "No page-level background selected."}
                        {pageBackgroundType === "colour" && `Solid colour background: ${pageSettingsDraft.PageBackgroundColour ?? ""}`}
                        {pageBackgroundType === "image" && `Background image with ${pageSettingsDraft.PageBackgroundImageStyle ?? "cover"}, ${pageSettingsDraft.PageBackgroundImagePosition ?? "center center"}, ${pageSettingsDraft.PageBackgroundImageRepeat ?? "no-repeat"}`}
                        {pageBackgroundType === "colour-image" && `Colour + image background using ${pageSettingsDraft.PageBackgroundColour ?? ""} with ${pageSettingsDraft.PageBackgroundImageStyle ?? "cover"}`}
                      </div>
                      <div className={styles.pageSettingsPreviewHint}>
                        Section background colours, images, and section classes will visually override these settings inside each section.
                      </div>
                    </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey="scripts">
                    <div className={styles.pageSettingsSection}>
                      <FormLabel>Page Script</FormLabel>
                      <p className={styles.pageSettingsEditorIntro}>
                        Add JavaScript that should run only for this page version, such as small one-off interactions or tracking tied to this layout.
                      </p>
                      <AceEditor
                        mode="javascript"
                        theme="sqlserver"
                        name="PageScript"
                        onChange={(value) => updatePageSettingsDraft("PageScript", value)}
                        fontSize={12}
                        value={pageSettingsDraft.PageScript ?? ""}
                        className="border"
                        setOptions={{
                          enableBasicAutocompletion: true,
                          enableLiveAutocompletion: true,
                          enableSnippets: true,
                          useWorker: false,
                          showPrintMargin: false
                        }}
                        style={{ width: "100%", height: "260px" }}
                      />
                      <p className={styles.pageSettingsHint}>
                        Use this for version-specific script only. Keep shared behavior in global site scripts where possible.
                      </p>
                    </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey="styles">
                    <div className={styles.pageSettingsSection}>
                      <FormLabel>Page Style</FormLabel>
                      <p className={styles.pageSettingsEditorIntro}>
                        Add CSS for this page version when the styling belongs to the page as a whole rather than an individual section or component.
                      </p>
                      <AceEditor
                        mode="css"
                        theme="sqlserver"
                        name="PageStyle"
                        onChange={(value) => updatePageSettingsDraft("PageStyle", value)}
                        fontSize={12}
                        value={pageSettingsDraft.PageStyle ?? ""}
                        className="border"
                        setOptions={{
                          enableBasicAutocompletion: true,
                          enableLiveAutocompletion: true,
                          enableSnippets: true,
                          useWorker: false,
                          showPrintMargin: false
                        }}
                        style={{ width: "100%", height: "260px" }}
                      />
                      <p className={styles.pageSettingsHint}>
                        Page-level styles affect the version wrapper first. Section classes and section styles can still override them lower down.
                      </p>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </div>
          )}
        </AppModal>
      )}

    </div>
  );
}

export default Designer;
